/**
 * Motor de cores do mini-site.
 * Mesma lógica aprovada no Cartão de Avaliação: lê a paleta da logo e veste
 * o site em 2 tons (primária + secundária de matiz distinta), com contraste
 * calculado — nunca "chuta" cor.
 */

/* ---------- utilidades de cor ---------- */
export const lum = (hex) => {
  const n = parseInt(String(hex).replace('#', ''), 16);
  if (isNaN(n)) return 0.5;
  return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
};

export const matiz = (hex) => {
  const n = parseInt(String(hex).replace('#', ''), 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  let h = 0;
  if (d) {
    if (mx === r) h = ((g - b) / d) % 6; else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4;
    h *= 60; if (h < 0) h += 360;
  }
  return h;
};

/** Mistura duas cores (t = 0..1 em direção a `b`). */
export function mix(a, b, t) {
  const A = parseInt(String(a).replace('#', ''), 16), B = parseInt(String(b).replace('#', ''), 16);
  const r = Math.round(((A >> 16) & 255) * (1 - t) + ((B >> 16) & 255) * t);
  const g = Math.round(((A >> 8) & 255) * (1 - t) + ((B >> 8) & 255) * t);
  const bl = Math.round((A & 255) * (1 - t) + (B & 255) * t);
  return '#' + [r, g, bl].map(v => v.toString(16).padStart(2, '0')).join('');
}

/** Texto legível sobre uma cor de fundo. */
export const tintaSobre = (hex) => (lum(hex) > 0.6 ? '#141518' : '#ffffff');

/**
 * Extrai até 5 cores dominantes de uma imagem (logo). SÓ NO NAVEGADOR (usa canvas).
 * Retorna array de hex, da mais dominante/vibrante para a menos.
 */
export function extrairPaleta(dataUrl) {
  return new Promise((resolve) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const N = 56, cv = document.createElement('canvas');
          cv.width = N; cv.height = N;
          const ctx = cv.getContext('2d');
          ctx.drawImage(img, 0, 0, N, N);
          const d = ctx.getImageData(0, 0, N, N).data, baldes = {};
          for (let i = 0; i < d.length; i += 4) {
            const r = d[i], g = d[i + 1], b = d[i + 2], a = d[i + 3];
            if (a < 128) continue;
            if (r > 236 && g > 236 && b > 236) continue;      // ignora branco de fundo
            const k = (r >> 4) + '_' + (g >> 4) + '_' + (b >> 4);
            const sat = Math.max(r, g, b) - Math.min(r, g, b);
            if (!baldes[k]) baldes[k] = { r: 0, g: 0, b: 0, n: 0, s: 0 };
            const B = baldes[k]; B.r += r; B.g += g; B.b += b; B.n++; B.s += sat;
          }
          let arr = Object.values(baldes)
            .map(B => ({ r: Math.round(B.r / B.n), g: Math.round(B.g / B.n), b: Math.round(B.b / B.n), n: B.n, s: B.s / B.n }))
            .filter(c => c.n >= 2);
          arr.sort((x, y) => (y.n * (1 + 2 * y.s / 255)) - (x.n * (1 + 2 * x.s / 255)));
          const hex = c => '#' + [c.r, c.g, c.b].map(v => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0')).join('');
          const out = [];
          for (const c of arr) {
            if (out.every(o => Math.abs(o.r - c.r) + Math.abs(o.g - c.g) + Math.abs(o.b - c.b) > 60)) out.push(c);
            if (out.length >= 5) break;
          }
          resolve(out.map(hex));
        } catch { resolve([]); }
      };
      img.onerror = () => resolve([]);
      img.src = dataUrl;
    } catch { resolve([]); }
  });
}

/**
 * Veste o site na paleta da marca — como um designer faria:
 *  - primária (dominante) => accent: botões e detalhes
 *  - secundária (matiz distinta) => acento de apoio (selo/realces)
 *  - tinta do botão calculada por contraste
 *  - fundo do QR/cartões recebe um tom bem leve da marca
 */
export function paletaParaCores(paleta, tpl) {
  if (!paleta || !paleta.length) return {};
  const primaria = paleta[0];
  let secundaria = null;
  for (let i = 1; i < paleta.length; i++) {
    let d = Math.abs(matiz(paleta[i]) - matiz(primaria));
    d = Math.min(d, 360 - d);
    if (d > 28) { secundaria = paleta[i]; break; }
  }
  const fundoClaro = lum(String(tpl?.bg || '#ffffff').startsWith('#') ? tpl.bg : '#ffffff') > 0.5;
  const legivel = (h) => {
    if (!h) return null;
    const L = lum(h);
    return fundoClaro ? (L < 0.62 ? h : null) : (L > 0.4 ? h : null);
  };
  return {
    accent: primaria,
    onAccent: tintaSobre(primaria),
    accent2: legivel(secundaria) || legivel(primaria) || undefined,
    star: '#FBBC04',                                   // estrelas sempre douradas (padrão Google)
    tintaMarca: mix(primaria, '#ffffff', 0.88),        // fundo suave com a cara da marca
  };
}

/** Junta: tokens do modelo + cores automáticas da logo + ajustes manuais do cliente. */
export function mesclarTema(tpl, cores = {}) {
  return { ...tpl, ...Object.fromEntries(Object.entries(cores).filter(([, v]) => v)) };
}

/** Paleta manual pronta para os seletores do editor. */
export const CORES_RAPIDAS = [
  '#1a73e8', '#0b57d0', '#2f7d4f', '#12a46b', '#c9a24b', '#b4552f',
  '#e0729b', '#c0392b', '#f5852b', '#ffd400', '#8a6bff', '#2fb4ff',
  '#202124', '#5f6368', '#ffffff',
];
